import { cva, type VariantProps } from 'class-variance-authority';
import { AlertTriangle, Info } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { initials } from '@/lib/format';

export const badgeVariants = cva('inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium [&_svg]:size-3', {
  variants: {
    tone: {
      neutral: 'bg-surface-hover text-muted',
      accent: 'bg-accent-soft text-accent',
      success: 'bg-success-soft text-success',
      warning: 'bg-warning-soft text-warning',
      danger: 'bg-danger-soft text-danger',
      info: 'bg-info-soft text-info',
    },
  },
  defaultVariants: { tone: 'neutral' },
});
export type Tone = NonNullable<VariantProps<typeof badgeVariants>['tone']>;

export const Badge = ({ children, tone, className, dot }: { children: ReactNode; tone?: Tone; className?: string; dot?: boolean }) => (
  <span className={cn(badgeVariants({ tone }), className)}>
    {dot ? <span aria-hidden className="size-1.5 rounded-full bg-current" /> : null}
    {children}
  </span>
);

export const Card = ({ children, className, title, actions, padded = true }: { children: ReactNode; className?: string; title?: ReactNode; actions?: ReactNode; padded?: boolean }) => (
  <section className={cn('rounded-lg border bg-surface shadow-card', className)}>
    {title || actions ? (
      <header className="flex items-center justify-between gap-2 border-b px-4 py-2.5">
        <h2 className="text-sm font-semibold">{title}</h2>
        {actions ? <div className="flex items-center gap-1">{actions}</div> : null}
      </header>
    ) : null}
    <div className={cn(padded && 'p-4')}>{children}</div>
  </section>
);

/** Page title row: title, optional subtitle/meta and actions on the right. */
export const PageHeader = ({ title, subtitle, actions, icon, className }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; icon?: ReactNode; className?: string }) => (
  <div className={cn('flex flex-wrap items-start justify-between gap-3 pb-4', className)}>
    <div className="flex min-w-0 items-center gap-3">
      {icon ? <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent [&_svg]:size-5">{icon}</div> : null}
      <div className="min-w-0">
        <h1 className="truncate text-lg font-semibold tracking-tight">{title}</h1>
        {subtitle ? <div className="mt-0.5 text-sm text-muted">{subtitle}</div> : null}
      </div>
    </div>
    {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
  </div>
);

export const EmptyState = ({ icon, title, description, action, className }: { icon?: ReactNode; title: string; description?: ReactNode; action?: ReactNode; className?: string }) => (
  <div className={cn('flex flex-col items-center justify-center px-6 py-14 text-center', className)}>
    {icon ? <div className="mb-3 flex size-11 items-center justify-center rounded-full bg-surface-hover text-subtle [&_svg]:size-5">{icon}</div> : null}
    <p className="text-sm font-medium">{title}</p>
    {description ? <p className="mt-1 max-w-sm text-sm text-muted">{description}</p> : null}
    {action ? <div className="mt-4">{action}</div> : null}
  </div>
);

export const Avatar = ({ name, className }: { name: string | null | undefined; className?: string }) => (
  <span aria-hidden className={cn('inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[11px] font-semibold text-accent', className)}>
    {initials(name)}
  </span>
);

/** Key/value pairs (record detail panels). */
export const DescriptionList = ({ items, className }: { items: { label: string; value: ReactNode; hidden?: boolean }[]; className?: string }) => (
  <dl className={cn('grid grid-cols-[minmax(110px,auto)_1fr] gap-x-4 gap-y-2 text-sm', className)}>
    {items
      .filter((i) => !i.hidden)
      .map((i) => (
        <div key={i.label} className="contents">
          <dt className="text-muted">{i.label}</dt>
          <dd className="min-w-0 break-words">{i.value === null || i.value === undefined || i.value === '' ? <span className="text-subtle">—</span> : i.value}</dd>
        </div>
      ))}
  </dl>
);

/** Inline banner, e.g. the organization alert (IT Glue "alert" strip). */
export const Banner = ({ children, tone = 'warning', className }: { children: ReactNode; tone?: 'warning' | 'info' | 'danger'; className?: string }) => (
  <div
    role={tone === 'danger' ? 'alert' : 'status'}
    className={cn(
      'flex items-start gap-2 rounded-lg border px-3 py-2 text-sm',
      tone === 'warning' && 'border-warning/30 bg-warning-soft text-warning',
      tone === 'danger' && 'border-danger/30 bg-danger-soft text-danger',
      tone === 'info' && 'border-info/30 bg-info-soft text-info',
      className,
    )}
  >
    {tone === 'info' ? <Info className="mt-0.5 size-4 shrink-0" /> : <AlertTriangle className="mt-0.5 size-4 shrink-0" />}
    <div className="min-w-0">{children}</div>
  </div>
);

export const Kbd = ({ children }: { children: ReactNode }) => (
  <kbd className="rounded border border-border-strong bg-surface-2 px-1.5 py-0.5 font-mono text-[10px] text-muted">{children}</kbd>
);

export const Stat = ({ label, value, hint, href }: { label: string; value: ReactNode; hint?: ReactNode; href?: string }) => {
  const body = (
    <>
      <div className="text-xs font-medium text-muted">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums tracking-tight">{value}</div>
      {hint ? <div className="mt-0.5 text-xs text-subtle">{hint}</div> : null}
    </>
  );
  return href ? (
    <a href={href} className="block rounded-lg border bg-surface p-4 shadow-card transition-colors hover:border-accent/40">
      {body}
    </a>
  ) : (
    <div className="rounded-lg border bg-surface p-4 shadow-card">{body}</div>
  );
};
